import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react'
import { useForm, FieldValues } from 'react-hook-form';
import toast from 'react-hot-toast';
import { RiEyeFill, RiEyeOffFill } from 'react-icons/ri';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { TermsCondition } from '../components/auth';
import { Loader } from '../components/shared';
import usePasswordVisibility from '../hooks/usePasswordVisibility';
import { AppPage } from '../layouts/types';
import { RootState, AppDispatch } from '../store';
import { signup } from '../store/reducers/authentication';
import { SignupFormSchema } from '../zod.schema';
import { MdCancel, MdCheckCircle } from 'react-icons/md';
import Icon from 'assets/icons';
type FormData = z.infer<typeof SignupFormSchema>;

const Signup: AppPage = () => {
  const passwordRules = [
    { label: "At least 8 characters", test: (pwd: string) => pwd.length >= 8 },
    { label: "One uppercase letter", test: (pwd: string) => /[A-Z]/.test(pwd) },
    { label: "One lowercase letter", test: (pwd: string) => /[a-z]/.test(pwd) },
    { label: "One number", test: (pwd: string) => /\d/.test(pwd) },
    { label: "One special character (!”#$%&'()*?@\\)", test: (pwd: string) => /[!"#$%&'()*?@\\]/.test(pwd) },
  ];

  const [password, setPassword] = useState('');
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const { showPassword, togglePasswordVisibility } = usePasswordVisibility();
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false); // Toogle Terms and Dailougue Window
  const {
    showPassword: showConfirmPassword,
    togglePasswordVisibility: toggleConfirmPasswordVisibility,
  } = usePasswordVisibility();

  const auth = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailFromParams = queryParams.get("emailid") || "";

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(SignupFormSchema) });

  const router = useNavigate();


  const handleSignup = async (data: FieldValues) => {
    if (data.isAgreementChecked) {
      const response: any = await dispatch(signup({ firstName: data.firstName, lastName: data.lastName, email: data.email, password: data.password }))

      if (response.payload.user)
        router(`/verify-otp?title=${"Enter Verification Code"}&type=${"signup"}&userId=${response?.payload.user?.id}`);
      else toast.error(auth.error)
    }
    else toast.error('Please agree the Terms and Conditions');
  }

  useEffect(() => {
    if (emailFromParams) {
      setValue("email", emailFromParams);
    }
  }, [emailFromParams, setValue]);



  return (
    <div className="flex justify-center items-center min-h-screen mobile:items-center">
      <div className="px-20 mobile:px-2 tablet:px-6 mobile:py-2">
        <div className="mb-6 flex justify-center  ">
          <img width={252} height={64} src={Icon.secondLogo} alt="Icon Image" className='mobile:w-[152px] mobile:h-[56px]' />
        </div>
        <h2 className="text-[#3B406E] mb-1 text-xl font-normal text-left mobile:text-center">
          SIGN UP
        </h2>
        <form
          className="space-y-2 mobile:p-0  tablet:p-1 w-full"
          onSubmit={handleSubmit(handleSignup)}
        >
          <div className="flex flex-row justify-between mobile:flex-col tablet:flex-row gap-3">
            <div className="flex flex-1 flex-col mobile:items-center">
              <input
                className="h-10 w-full rounded-[8px] mobile:w-[300px]  bg-white shadow-[0_0_8px_2px_rgba(95,72,138,0.16)] text-base outline-none px-6"
                type="text"
                id="firstName"
                placeholder="First Name"
                {...register("firstName")}
              />
              {errors.firstName?.message && (
                <span className="text-red-500">{errors.firstName?.message}</span>
              )}
            </div>

            <div className="flex flex-1 flex-col mobile:items-center">
              <input
                className="h-10 w-full rounded-[8px] mobile:w-[300px] bg-white shadow-[0_0_8px_2px_rgba(95,72,138,0.16)] text-base outline-none px-4 mobile:px-4 tablet:px-5"
                type="text"
                id="lastName"
                placeholder="Last Name"
                {...register("lastName")}
              />
              {errors.lastName?.message && (
                <span className="text-red-500">{errors.lastName?.message}</span>
              )}
            </div>
          </div>

          <div className="flex flex-col mobile:items-center">
            <input
              className="h-10 w-full rounded-[8px]  mobile:w-[300px] bg-white shadow-[0_0_8px_2px_rgba(95,72,138,0.16)] text-base outline-none px-4 mobile:px-4 tablet:px-5"
              type="text"
              id="email"
              placeholder="Email"
              {...register("email")}
              disabled={!!emailFromParams}
            />
            {errors.email?.message && (
              <span className="text-red-500">{errors.email?.message}</span>
            )}
          </div>

          <div className="flex flex-col mobile:items-center">
            <p className="text-base text-[#3B406E] font-normal mb-2">
              <span className="text-app-blue font-bold">Create </span>
              <span className="text-app-blue font-normal">Password </span>
            </p>
            <div className="relative  mobile:w-[300px]">
              <input
                placeholder="Password"
                {...register("password")}
                className="h-10 w-full rounded-[8px] bg-white shadow-[0_0_8px_2px_rgba(95,72,138,0.16)] text-base outline-none px-4 mobile:px-2 tablet:px-5"
                type={showPassword ? "text" : "password"}
                onChange={(e) => {
                  const value = e.target.value;
                  setPassword(value);

                  if (!isPasswordTouched && value.length > 0) {
                    setIsPasswordTouched(true);
                  }

                  // Reset touch flag if input is cleared
                  if (value.length === 0) {
                    setIsPasswordTouched(false);
                  }
                }}
              />
              <span
                className="absolute right-4 top-1/2 -translate-y-1/2"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <RiEyeFill color="#D4D6DB" size={25} />
                ) : (
                  <RiEyeOffFill color="#D4D6DB" size={25} />
                )}
              </span>
            </div>

            {errors.password?.message && (
              <span className="text-red-500">{errors.password?.message}</span>
            )}

            <div className="mt-2">
              {isPasswordTouched && password.length > 0 && (
                <div>
                  {passwordRules.map((rule, idx) => {
                    const passed = rule.test(password);
                    return (
                      <p
                        key={idx}
                        className={`text-sm flex items-center gap-2 ${passed ? "text-green-600" : "text-red-500"
                          }`}
                      >
                        {passed ? (
                          <MdCheckCircle className="text-green-600" size={18} />
                        ) : (
                          <MdCancel className="text-red-500" size={18} />
                        )}
                        {rule.label}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>

          </div>

          <div className="flex flex-col mobile:items-center">
            <div className="relative  mobile:w-[300px]">
              <input
                placeholder="Confirm Password"
                {...register("confPassword")}
                className="h-10 w-full rounded-[8px] bg-white shadow-[0_0_8px_2px_rgba(95,72,138,0.16)] text-base outline-none px-4 mobile:px-4 tablet:px-5"
                type={showConfirmPassword ? "text" : "password"}
              />
              <span
                className="absolute right-6 top-[25%]"
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassword ? (
                  <RiEyeFill color="#D4D6DB" size={25} />
                ) : (
                  <RiEyeOffFill color="#D4D6DB" size={25} />
                )}
              </span>
            </div>

            {errors.confPassword?.message && (
              <span className="text-red-500">{errors.confPassword.message}</span>
            )}
          </div>

          <div className="flex items-start mobile:w-[300px] whitespace-nowrap mobile:text-sm mobile:items-center tablet:items-center  text-base text-[#3B406E] font-normal">
            <input
              type="checkbox"
              id="agreeCheckbox"
              {...register('isAgreementChecked')}
              className="h-4 w-4 mr-2 mt-1  accent-[#3B406E] focus:ring-0 bg-white shadow-[0_0_8px_2px_rgba(95,72,138,0.16)] outline-none"
            />
            <label htmlFor="agreeCheckbox">I agree to the </label>
            <span
              className="ml-1 cursor-pointer"
              onClick={() => setIsTermsDialogOpen(true)}
            >
              Terms of Service and Privacy Policy
            </span>
          </div>
          {/* Add this span for displaying the error message */}
          {errors.isAgreementChecked && (
            <span className="text-red-500">{errors.isAgreementChecked.message}</span>
          )}

          <div className="flex flex-row justify-between mobile:items-center mobile:flex-col mobile:space-y-3 space-x-4 mobile:space-x-0 items-baseline">

            <div className="flex ">
              <button
                className="flex justify-center bg-[#3B406E] shadow text-white font-bold px-20 mobile:px-4 tablet:px-10 hover:scale-105 w-full py-1 rounded-full" type="submit"
                disabled={auth.isLoading}
              >
                <span className="whitespace-nowrap">
                  Sign up
                  {auth.isLoading && <Loader size={30} />}
                </span>
              </button>
            </div>

          </div>
          <div className="mobile:hidden text-base mobile:items-center mobile:text-sm flex-wrap " >
            <span >
              Already have an account with us? &nbsp;
              <Link className="font-bold text-app-blue underline" to={"/login"}>
                Login
              </Link>
            </span>
          </div>
        </form>

        <div className="flex items-center justify-between gap-1 mt-2  px-2 w-full h-[44px] rounded-lg bg-white shadow-[0_0_2px_1px_rgba(0,0,0,0.25)] 
            overflow-hidden mobile:flex mobile:h-[48px] mobile:py-2 mobile:w-[266px] mobile:mx-auto mobile:justify-center mobile:item-start mobile:flex-col" >

          <button
            type="button"
            className="flex items-center justify-center gap-1 text-[#3B406E] font-bold text-[14px] w-[162px] max-w-auto h-[44px] rounded-lg bg-[#E9BE50] whitespace-nowrap p-3
              relative -ml-3 mobile:w-[286px] mobile:-mt-1 mobile:ml-0  mobile:h-[30px]">

            <img src={"img/SupportAgent.svg"} alt="support" className="w-5 h-5" />
            Support Center
          </button>

          <div className="w-full flex flex-row">
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=support@viliyo.com&su=Support%20Request&body=Hello%20Team,"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 text-[#3B406E] text-[12px] font-bold whitespace-nowrap mx-auto mobile:w-full mobile:justify-start"
            >
              <img src={"img/attach_email.svg"} alt="email" className="w-5 h-5" />
              support@viliyo.com
            </a>

            <a
              href="tel:+918976724006"
              className="flex items-center text-[#3B406E] text-[12px] font-bold whitespace-nowrap mobile:w-full mobile:justify-end"
            >
              <img src={"img/call.svg"} alt="phone" className="w-5 h-5" />
              +91 89767 24006
            </a>
          </div>
        </div>

        <div>
          {isTermsDialogOpen && <TermsCondition onClose={() => setIsTermsDialogOpen(false)} />}
        </div>
      </div>
    </div>
  );


};

export default Signup;